import React, { useState, useEffect } from "react";
import { X, Calendar, Clock } from "lucide-react";

export default function RowModal({ isOpen, onClose, onSubmit, columns, row }) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (row) {
      // Load existing row data
      const loadedData = {};
      Object.keys(row.data || {}).forEach((key) => {
        const col = columns.find((c) => c.column_key === key);
        let value = row.data[key];

        // datetime 형식 맞추기 (초가 없으면 추가)
        if (col && col.data_type === "datetime" && value) {
          // YYYY-MM-DDTHH:MM 형식으로 변환
          if (typeof value === "string") {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              const hours = String(date.getHours()).padStart(2, "0");
              const minutes = String(date.getMinutes()).padStart(2, "0");
              const seconds = String(date.getSeconds()).padStart(2, "0");
              value = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            }
          }
        }

        loadedData[key] = value;
      });
      setFormData(loadedData);
    } else {
      // Initialize empty form
      const initialData = {};
      columns.forEach((col) => {
        if (!["id", "created_at", "updated_at"].includes(col.column_key)) {
          initialData[col.column_key] = getDefaultValue(col);
        }
      });
      setFormData(initialData);
    }
  }, [columns, row]);

  if (!isOpen) return null;

  const getDefaultValue = (column) => {
    switch (column.data_type) {
      case "boolean":
        return false;
      case "number":
      case "double":
        return "";
      case "date":
      case "datetime":
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const newErrors = {};

    columns.forEach((col) => {
      if (
        col.is_required &&
        !["id", "created_at", "updated_at"].includes(col.column_key)
      ) {
        const value = formData[col.column_key];
        if (value === undefined || value === null || value === "") {
          newErrors[col.column_key] = `${col.display_name}은(는) 필수입니다.`;
        }
      }

      // Type-specific validation
      if (
        formData[col.column_key] !== "" &&
        formData[col.column_key] !== null
      ) {
        if (
          col.data_type === "number" &&
          isNaN(Number(formData[col.column_key]))
        ) {
          newErrors[col.column_key] = "올바른 숫자를 입력하세요.";
        }
        if (
          col.data_type === "double" &&
          isNaN(parseFloat(formData[col.column_key]))
        ) {
          newErrors[col.column_key] = "올바른 소수를 입력하세요.";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    // Process data based on column types
    const processedData = {};
    columns.forEach((col) => {
      if (
        !["id", "created_at", "updated_at", "formula"].includes(col.column_key)
      ) {
        const value = formData[col.column_key];

        if (value === "" || value === null || value === undefined) {
          processedData[col.column_key] = null;
        } else {
          switch (col.data_type) {
            case "number":
              processedData[col.column_key] = parseInt(value, 10);
              break;
            case "double":
              processedData[col.column_key] = parseFloat(value);
              break;
            case "boolean":
              processedData[col.column_key] = Boolean(value);
              break;
            default:
              processedData[col.column_key] = value;
          }
        }
      }
    });

    setSubmitting(true);
    try {
      await onSubmit(processedData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const renderField = (column) => {
    const value = formData[column.column_key] ?? "";
    const error = errors[column.column_key];

    switch (column.data_type) {
      case "text":
        return (
          <input
            type="text"
            value={value}
            onChange={(e) =>
              setFormData({ ...formData, [column.column_key]: e.target.value })
            }
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? "border-red-300" : "border-gray-300"
            }`}
            disabled={submitting}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) =>
              setFormData({ ...formData, [column.column_key]: e.target.value })
            }
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? "border-red-300" : "border-gray-300"
            }`}
            disabled={submitting}
          />
        );

      case "double":
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) =>
              setFormData({ ...formData, [column.column_key]: e.target.value })
            }
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? "border-red-300" : "border-gray-300"
            }`}
            disabled={submitting}
          />
        );

      case "date":
        return (
          <div className="relative">
            <input
              type="date"
              value={value}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  [column.column_key]: e.target.value,
                })
              }
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                error ? "border-red-300" : "border-gray-300"
              }`}
              disabled={submitting}
            />
            <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        );

      case "datetime":
        return (
          <div className="relative">
            <input
              type="datetime-local"
              step="1"
              value={value}
              onChange={(e) => {
                let val = e.target.value;
                // datetime-local에서 초가 없으면 :00 추가
                if (val && val.length === 16) {
                  val = val + ":00";
                }
                setFormData({ ...formData, [column.column_key]: val });
              }}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                error ? "border-red-300" : "border-gray-300"
              }`}
              disabled={submitting}
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        );

      case "boolean":
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  [column.column_key]: e.target.checked,
                })
              }
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={submitting}
            />
            <span className="text-sm text-gray-700">활성화</span>
          </label>
        );

      case "select":
        const options = column.settings?.options || [];
        return (
          <select
            value={value}
            onChange={(e) =>
              setFormData({ ...formData, [column.column_key]: e.target.value })
            }
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? "border-red-300" : "border-gray-300"
            }`}
            disabled={submitting}
          >
            <option value="">선택하세요</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      default:
        return null;
    }
  };

  // Filter out system columns
  const editableColumns = columns.filter(
    (col) => !["id", "created_at", "updated_at"].includes(col.column_key)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {row ? "데이터 수정" : "데이터 추가"}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]"
        >
          <div className="space-y-5">
            {editableColumns.map((column) => (
              <div key={column.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {column.display_name}
                  {column.is_required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                {renderField(column)}
                {errors[column.column_key] && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors[column.column_key]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting
                ? row
                  ? "수정 중..."
                  : "추가 중..."
                : row
                ? "수정"
                : "추가"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
